import { Modal } from 'antd'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { info } from 'winston'

import { getProductRoom } from '@api/hernest/roomIdea'
import { PageData } from '@api/home/detail/types'
import { useSku } from '@api/home/detail/useSku'
import { useSearch } from '@hooks'
import { useElementCustomAction } from '@hooks/useElementCustomAction'
import useGlobalEvent, { dispatchGlobalEvent } from '@hooks/useGlobalEvent'
import useQuickView from '@hooks/useQuickView'
import showPopup from '@ui-pc/Popup'
import { getRelativeUrl } from '@utils'
import {
  getSearchParams,
  getUrlQuery,
  injectScript,
  isHomeProject,
  isMobileProject,
  pushDetailHistory
} from '@utils/index'
import { request } from '@utils/request'
import { Knife, Type } from '@utils/tool'
import { context } from '@utils/tool/khook'
import winPixel from '@utils/winPixel'

import SSR_API from '../ssrAction'

type Ref = {
  current: ReturnType<typeof useSku> & {
    info: PageData['productInfo']
    isActive
    onChangeRelateSku?: Function
  } & {
    skuData: Omit<
      ReturnType<typeof useSku>,
      'isLoading' | 'pageData' | 'isError'
    >
  }
  cart: {
    isSoldOut
    onBuyNow
    onAddCart(
      data?: {
        productsId
        skuId
        quantity: number
      },
      params?: { [key: string]: any },
      callback?: Function,
      winPixelParams?: { [key: string]: any }
    )
  }
  widgetCtx: {
    setState
  }
  drawer: {
    openWeights: Function
  }
  /**
   * 3d区域view 实列
   */
  viewer: { destory; reloading }
}

type Props = {
  small?: boolean
  readonly?: boolean
  productsSkuId: string
  url?: string
}

export type UProps = Props & ReturnType<typeof useDetail>

export const CURRENT = 'current' as const

export const ctx = context<any, Ref>({})

export const getSkuId = (pathname) => {
  let productsSkuId = ''
  if (
    pathname.search(/^\/(products|item|quickView|items)\/.*-p-\d+.html/i) > -1
  ) {
    productsSkuId = pathname
      .match(/-p-\d+.html/)[0]
      .substring(3)
      .split('.')[0]
  }
  return productsSkuId
}

const structuredScriptId = 'structuredScriptId_' + Knife.randomString(8)

const injectStructuredData = (
  info: PageData['productInfo'],
  activityInfo: PageData['activityInfo']
) => {
  let structuredScript = document.getElementById(
    structuredScriptId
  ) as HTMLScriptElement

  if (!structuredScript) {
    structuredScript = document.createElement('script')
    structuredScript.type = 'application/ld+json'
    structuredScript.id = structuredScriptId
    document.getElementsByTagName('html')[0].appendChild(structuredScript)
  }

  const obj = {
    '@context': 'https://schema.org/', //写死
    '@type': 'Product', //写死
    sku: info.poa, //sku编码        data.product_info.poa
    height: info.specInfo.specItemList?.find((data) =>
      data.name?.match(/height/i)
    )?.value,
    width: info.specInfo.specItemList?.find((data) =>
      data.name?.match(/width/i)
    )?.value,
    depth: info.specInfo.specItemList?.find((data) =>
      data.name?.match(/depth/i)
    )?.value,
    weight: info.specInfo.specItemList?.find((data) =>
      data.name?.match(/weight/i)
    )?.value,
    color: info.saleAttrs?.find((data) =>
      data.productsOptionsName?.match(/color/i)
    )?.productsOptionsValuesName,
    image: info.imageList
      ?.filter((data) => data.type == 1)
      .map((data) => data.imagePath),
    name: info?.productsName, //产品标题 data.product_info.products_name
    description: info?.feedDesc,
    gtin: '',
    brand: {
      '@type': 'Brand', //写死
      name: info?.brandName
    },
    //以下红色字体都是嵌套在 offer，不管有无闪购
    offers: {
      '@type': 'Offer', //写死
      SKU: info.poa,
      url: info.url,
      itemCondition: 'https://schema.org/NewCondition', //全新产品，写死
      availability:
        info.productsStatus === 2
          ? 'http://schema.org/OutOfStock'
          : 'http://schema.org/InStock', //根据后台库存判断,https://schema.org/ItemAvailability 所有值看左边这个链接，但是我们只需要可售值为 InStock ，不可售值为 OutOfStock ，
      price: info.usPrice, //取前台最终销售价，不带单位 data.product_info.sale_price
      priceCurrency: info.currency, //写死 data.product_info.currency
      priceValidUntil: activityInfo?.endTime
        ? new Date(activityInfo?.endTime * 1000)
            ?.toLocaleDateString()
            ?.replaceAll('/', '-') || ' '
        : ' ', //优惠价格失效日期，2024-12-31 格式，有就填写，没就不填写
      //嵌套在Offer里面
      shippingDetails: {
        '@type': 'OfferShippingDetails', //写死
        shippingDestination: {
          '@type': 'DefinedRegion', //写死
          addressCountry: 'US' //写死
        },

        shippingRate: {
          '@type': 'MonetaryAmount', //写死
          value: 0, //写死
          currency: 'USD', //写死

          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy', //写死
            applicableCountry: 'US', //写死
            returnPolicyCategory:
              'https://schema.orgMerchantReturnFiniteReturnWindow', //写死
            merchantReturnDays: 30, //写死
            returnMethod: 'https://schema.org/ReturnByMail', //写死
            returnFees: 'https://schema.org/FreeReturn' //写死
          }
        }
      }
    },
    // review: {
    //   '@type': 'Review' //写死
    // },
    aggregateRating: {
      '@type': 'AggregateRating', //写死
      ratingValue: info.reviewScore, //平均得分 data.product_info.review_score
      reviewCount: info.reviewNum //点评数量 data.product_info.review_num
    }
  }

  //console.log('obj', obj)
  structuredScript.innerHTML = JSON.stringify(obj)
}

const REDIRECT_LIST = {
  '2353': '/category/sofas-c-315.html'
}

const redirectForId = (id: string | number, jumpTo: Function) => {
  if (REDIRECT_LIST[id]) {
    jumpTo(REDIRECT_LIST[id])
    return
  }
}

export const useDetail = (
  props: ServerFC<PageData> & Props,
  reqUrl = 'products',
  extra = {}
) => {
  const navigate = useNavigate()
  const location = useLocation()

  const id = getSkuId(props.url || location.pathname)

  // 临时处理，指定ID重定向到指定链接
  !isHomeProject && redirectForId(id, navigate)

  const hookData = useSku(props.pageData, reqUrl, extra)

  const { isError, pageData, isLoading, ...rest } = hookData

  const info: PageData['productInfo'] = Type.object(pageData?.productInfo)

  const ref = ctx.useRef()

  const isActive = pageData?.activityInfo?.isStarted === 1

  // 获取 room idea 数据
  const { data: roomIdea } = useSearch(
    () => reqUrl === 'products' && getProductRoom(id),
    [id]
  )

  // 埋点
  useEffect(() => {
    if (Type.isEmpty(pageData?.productInfo)) return
    pageData && winPixel.meta.viewContent(pageData?.productInfo)
  }, [JSON.stringify(pageData)])

  useEffect(() => {
    if (Type.isEmpty(info)) {
      return
    }

    try {
      injectStructuredData(info, pageData.activityInfo)
    } catch (error) {}

    const injectData = {
      skuId: info.productsSkuId,
      activityInfo: pageData.activityInfo,
      productTag: pageData.productActTag,
      productsName: info.productsName,
      jumpUrl: location.pathname,
      formatFinalPrice: info.formatSalePrice,
      formatMarketPrice: info.formatMarketPrice,
      saveMoneyDesc: info.saveMoneyDesc,
      starScore: info.starScore,
      reviewNum: info.reviewNum,
      mainImage: info.imageList?.[0]?.imagePath,
      hoverImage: info.imageList?.[1]?.imagePath
    }
    pushDetailHistory(injectData)
  }, [location.pathname])

  //动态显示加购按钮
  useEffect(() => {
    //
    if (reqUrl !== 'products' && isMobileProject) {
      if (reqUrl === 'items') {
        //skip
      } else {
        return
      }
    }

    //console.log('error', reqUrl, isMobileProject)

    function isInViewport() {
      const ratio = window.innerWidth / 750

      const element = document.getElementById('detail_middle_view')
      // 首先，获取元素的位置和大小
      const rect = element.getBoundingClientRect()

      // 然后，计算元素的顶部和底部位置
      const top = rect.top

      //console.log('top', top)
      if (isMobileProject) {
        return top <= 56 * ratio
      }
      // 判断元素是否在可视区域内
      return top <= 100
    }

    function check() {
      try {
        const isView = isInViewport()
        const float = document.getElementById('top_add_cart')
        if (isView) {
          float.style.display = 'block'
          dispatchGlobalEvent('changeSubButtonPosition', { position: '20vw' })
        } else {
          float.style.display = 'none'
          dispatchGlobalEvent('changeSubButtonPosition')
        }
      } catch (error) {}
    }
    window.addEventListener('scroll', check)
    window.dispatchEvent(new Event('scroll'))
    return () => {
      window.removeEventListener('scroll', check)
    }
  }, [])

  const shopping = () => {
    dispatchGlobalEvent('shopping')
  }

  const onChangeRelateSku = (data, reqUrl = 'products') => {
    const { jumpUrl, skuid } = data || {}
    if (!jumpUrl || !skuid) {
      return
    }
    hookData.mutation(skuid).then(() => {
      if (reqUrl === 'quickView') return
      navigate(getRelativeUrl(jumpUrl).replace('products', reqUrl), {
        replace: true,
        state: { from: '/self', id: skuid }
      })
    })
  }

  ctx.useHandle(CURRENT, {
    ...hookData,
    skuData: rest,
    info,
    onChangeRelateSku,
    isActive
  })

  useGlobalEvent('detailRefetch', () => {
    hookData.mutation(info.productsSkuId)
  })

  return {
    ref,
    info,
    isActive,
    shopping,
    skuId: id,
    skuData: rest,
    pageData,
    isLoading,
    search: location.search,
    mutation: hookData.mutation,
    isError: [props.isError, isError].includes(true),
    roomIdea: roomIdea?.roomInfo || [],
    onChangeRelateSku
  }
}

export const useItemQuickView = (props: { url: string }) => {
  // const navigate = useNavigate()

  // const location = useLocation()

  const entrance = useSearch(
    () => SSR_API.product.action(request, props.url),
    []
  )

  // const id = getSkuId(props.url)

  const hookData = useSku(entrance.data, 'quickView')

  const { isError, pageData, isLoading, ...rest } = hookData

  const info: PageData['productInfo'] = Type.object(pageData?.productInfo)

  const isActive = pageData?.activityInfo?.isStarted === 1

  const onChangeRelateSku = (data, reqUrl = 'products') => {
    const { jumpUrl, skuid } = data || {}
    if (!jumpUrl || !skuid) {
      return
    }
    hookData.mutation(skuid)
    // .then(() => {
    //   if (reqUrl === 'quickView') return
    //   navigate(getRelativeUrl(jumpUrl).replace('products', reqUrl), {
    //     replace: true,
    //     state: { from: '/self', id: skuid }
    //   })
    // })
  }

  return {
    info,
    isActive,
    pageData,
    skuData: rest,
    onChangeRelateSku,
    skuId: info?.productsSkuId,
    isLoading: entrance.isLoading || isLoading,
    search: location.search,
    mutation: hookData.mutation,
    isError: [entrance.isError, isError].includes(true)
  }
}

export const useFeed = (props) => {
  const data = useDetail(props, 'item')
  const search = getSearchParams(data.search, ['cat_id'])

  const { open, drawer: quickViewBox, getProductData, close } = useQuickView()

  const cateId = search.cat_id || data.info?.categoryId
  //console.log('search', search)
  const setFull = () => {
    window.open(
      window.location.pathname.replace('item', 'products') +
        window.location.search
    )
  }

  const seeReviews = () => {
    // window.open(
    //   window.location.pathname.replace('item', 'products') +
    //     window.location.search +
    //     '#reviews'
    // )
    open({ jumpUrl: data.info.url, params: { handleOpen: { key: 'reviews' } } })
  }

  const { data: itemData } = useSearch(
    () =>
      data.skuId &&
      request.post('/product/getItemInfo', {
        terminal: isMobileProject ? 'm' : 'pc',
        products_sku_id: data.skuId
      }),
    [data.skuId]
  )

  useGlobalEvent('listAddCartFromFeed', (goods) => {
    // console.log('goods', goods)

    if (goods.skuList) {
      open({
        jumpUrl: goods.jumpUrl,
        footer() {
          return (
            <div
              className='bundle-picker-button-area'
              onClick={() => {
                //   onPickerQuickViewConfirm(getProductData()?.skuId)
                close()
              }}
            >
              <button
                className='ViewDetails'
                onClick={() => {
                  const info = getProductData().info as PageData['productInfo']
                  window.open(info.url, '__target')
                }}
              >
                View Details
              </button>
              <button
                className='Confirm'
                onClick={() => {
                  dispatchGlobalEvent(
                    'receiveAddCartFromFeed',
                    getProductData().info
                  )
                }}
              >
                Confirm
              </button>
            </div>
          )
        }
      })
      return
    }

    dispatchGlobalEvent('receiveAddCartFromFeed', goods)
  })

  return {
    ...data,
    open,
    quickViewBox,
    cateId,
    setFull,
    seeReviews,
    itemData: itemData?.data || {}
  }
}
